import { Grouper } from '../models/grouper';
import { Sorter } from '../models/sorter';
import { TreeNode } from '../models/tree-node';

export class Sorting {
    static _group(array: any[], sorters: Sorter[], grouper: Grouper, parent: TreeNode | null): TreeNode[] {
        if (!grouper.label) {
            grouper.label = grouper.property;
        }
        // If required, sort array using group property
        if (grouper.sort) {
            array = Sorting.sort(array, [
                new Sorter({
                    property: grouper.property
                })
            ]);
        } else {
            // Perform a first sort
            array = Sorting.sort(array, sorters);
        }

        // Then, sort each group individually
        let arrayExp: any[] = [];
        let arrayTmp: any[] = [];
        let oldValue = null;
        let newValue = null;
        let groupName = null;
        for (var i = 0; i < array.length; i++) {
            newValue = Sorting._resolve(array[i], grouper.property);
            // Initialize group name
            if (i === 0) {
                groupName = Sorting._resolve(array[i], grouper.label);
                oldValue = newValue;
            }
            // If first iteration or value changes
            if (i !== 0 && newValue !== oldValue) {
                // Sort subset
                arrayTmp = Sorting.sort(arrayTmp, sorters);
                // Append new group to export array
                let node: TreeNode = new TreeNode({
                    children: arrayTmp,
                    key: oldValue,
                    label: groupName,
                    level: !!parent ? parent.level + 1 : 0,
                    parent: parent,
                });
                arrayExp.push(node);
                // Init with new value
                oldValue = newValue;
                arrayTmp = [];
                groupName = Sorting._resolve(array[i], grouper.label);
            }

            // Push value in subset
            arrayTmp.push(array[i]);
        }
        // Last sort
        arrayTmp = Sorting.sort(arrayTmp, sorters);
        // Append new node to export array
        let node: TreeNode = new TreeNode({
            children: arrayTmp,
            key: newValue,
            label: groupName,
            level: !!parent ? parent.level + 1 : 0,
            parent: parent,
        });
        arrayExp.push(node);

        return arrayExp;
    }

    static _grouping(array: any[], sorters: Sorter[], groupers: Grouper[], parent: TreeNode | null) {
        // Build the group
        let treeNodes: TreeNode[] = Sorting._group(array, sorters, groupers[0], parent);
        // Remove first element
        groupers.shift();
        // If there is other groups to build
        if (groupers.length) {
            // Create sub groups in groups
            treeNodes.forEach(node => {
                let emptyArray: Grouper[] = []
                let groupersTmps: Grouper[] = emptyArray.concat(groupers);
                node.nodes = Sorting._grouping(node.children, sorters, groupersTmps, node);
            });
        }

        return treeNodes;
    }

    static _resolve(object: any, path: string) {
        var parts = path.split('.');
        var current = object;
        var next = null;
        while (parts.length) {
            let args: any[] = [];
            if (typeof current !== 'object') {
                return undefined;
            }
            // Detect if current path part is a function with parameters
            // Identify '(' position
            if (parts[0].includes('(')) {
                let subParts = parts[0].split('(');
                parts[0] = subParts[0];
                let sArgsString = subParts[1].substring(0, subParts[1].length - 1).replace(' ', '');
                args = sArgsString.split(',');
            }

            next = current[parts.shift() || 0];
            if (typeof next === 'function') {
                current = next.call(current, ...args);
            } else if (next === null || typeof next === 'undefined') {
                return null;
            } else {
                current = next;
            }
        }
        return current;
    }

    /**
     * Sort an array of elements
     * @param array Array to sort
     * @param sorters Sorting parameters
     */
    static sort(array: any[], sorters: Sorter[], groupers?: Grouper[], parent: TreeNode | null = null) {
        // Undefined array or Empty array
        if (!array || array.length === 0) {
            return [];
        }

        // No sorters
        if (!sorters || sorters.length === 0) {
            return array;
        }

        // If grouping is required
        if (!!groupers && groupers.length > 0) {
            return Sorting._grouping(array, sorters, groupers, parent);
        }

        // Parameters
        let property = sorters['0'].property;
        let length = array.length;
        let descending, emptyFirst;
        for (let key in sorters) {
            if (key === '0') {
                descending = !!sorters['0'].descending;
                emptyFirst = !!sorters['0'].emptyFirst;
                // Perform actual sorting
                array = Sorting._sort(array, property, descending, emptyFirst);
            } else {
                // Array has been sorted using first property, now we go deeper
                let newArray: any[] = [];
                let subset = [];
                let oldValue = null;
                let newValue = null;
                // Remove first sorter
                let newSortings = sorters.slice(1);
                // Split the array into subarrays with the same first sort criteria, and sort them
                for (let j = 0; j < length; j++) {
                    newValue = Sorting._resolve(array[j], property);
                    // If first iteration or value changes
                    if (j === 0 || newValue !== oldValue) {
                        // Sort subset
                        subset = Sorting.sort(subset, newSortings);
                        // Concat
                        newArray = newArray.concat(subset);
                        // Init with new value
                        oldValue = newValue;
                        subset = [];
                    }

                    // Push value in subset
                    subset.push(array[j]);
                }
                // Last sort
                subset = Sorting.sort(subset, newSortings);
                // Concat
                newArray = newArray.concat(subset);
                // No need to go further, other sorts will occur with recursivity
                return newArray;
            }
        }
        return array;
    }

    static _sort(array: any[], property: string, descending: boolean, emptyFirst: boolean) {
        let emptyArray: any[] = [];
        let arrayExp = emptyArray.concat(array);
        let length = arrayExp.length;
        let swapped;
        let value1;
        let value2;
        // Sort the whole array
        do {
            swapped = false;
            for (let i = 0; i < length - 1; i++) {
                value1 = Sorting._resolve(arrayExp[i], property);
                value2 = Sorting._resolve(arrayExp[i + 1], property);
                // If both values are null, skip iteration
                if (!value1 && !value2) {
                    continue;
                    // If value1 is null and null have to be at the beginning
                } else if (!value1 && emptyFirst) {
                    continue;
                    // If value2 is null and null have to be at the end
                } else if (!value2 && !emptyFirst) {
                    continue;
                    // If value1 is null and null have to be at the end
                } else if (!value1 && !emptyFirst) {
                    [arrayExp[i], arrayExp[i + 1]] = [arrayExp[i + 1], arrayExp[i]];
                    swapped = true;
                    // If value2 is null and null have to be at the beginning
                } else if (!value2 && emptyFirst) {
                    [arrayExp[i], arrayExp[i + 1]] = [arrayExp[i + 1], arrayExp[i]];
                    swapped = true;
                    // Neither value1 nor value2 is null
                } else if (!descending) {
                    // Ascending sort
                    if (value1 > value2) {
                        [arrayExp[i], arrayExp[i + 1]] = [arrayExp[i + 1], arrayExp[i]];
                        swapped = true;
                    }
                } else {
                    // Descending sort
                    if (value1 < value2) {
                        [arrayExp[i], arrayExp[i + 1]] = [arrayExp[i + 1], arrayExp[i]];
                        swapped = true;
                    }
                }
            }
        } while (swapped);

        return arrayExp;
    }
}
